/**
 * URL 状态（筛选 / 排序 / 分页 / 模式）的解析与序列化。
 *
 * 原则（详见 docs/frontend/ux-patterns.md）：
 *   1) URL 是列表筛选状态的唯一真相（single source of truth）——刷新、分享、前进后退都还原同一屏。
 *   2) 默认值不写入 URL：`/` 与 `/?q=shoes` 是两种不同的可分享状态，但 `?status=all` 这种
 *      等价于默认值的参数会被丢弃，URL 保持短、可读、可手写。
 *   3) 解析必须对垃圾输入免疫：任何字段坏了都回落到默认值，绝不抛错（用户会手改 URL）。
 *
 * 注意：`sort` 的取值就是 contracts 的字段名（spendCents / ctrPermille / shopifyRoas …），
 * 与 DerivedMetrics / MetricBucket 严格一致，见 src/data/columns.ts。
 */

export type AdsMode = "virtual" | "paged";
export type SortDir = "asc" | "desc";

export interface AdsSearch {
  /** 关键词：账户 / 系列 / 广告名称 */
  readonly q: string;
  /** 状态筛选，"all" 表示不筛 */
  readonly status: string;
  /** 账户筛选，账户 id 或 "all" */
  readonly account: string;
  /** 语言筛选（R24）：ISO-639-1 小写（`en`/`de`/`es`…），"all" 表示不筛。 */
  readonly language: string;
  /** Shopify ROAS 下限，0 表示不筛 */
  readonly minRoas: number;
  /** virtual = 虚拟化滚动全量；paged = 传统分页（用于对照与导出场景） */
  readonly mode: AdsMode;
  /** 排序列 id（= contracts 字段名） */
  readonly sort: string;
  readonly dir: SortDir;
  /** 分页模式下的页码，从 1 开始 */
  readonly page: number;
  /** 分页模式下每页行数 */
  readonly size: number;
}

export const ADS_PAGE_SIZES = [25, 50, 100, 200] as const;

export const DEFAULT_ADS_SEARCH: AdsSearch = {
  q: "",
  status: "all",
  account: "all",
  language: "all",
  minRoas: 0,
  mode: "virtual",
  sort: "spendCents",
  dir: "desc",
  page: 1,
  size: 50,
};

const MAX_SIZE = 500;

function readString(raw: Record<string, unknown>, key: string, fallback: string): string {
  const value = raw[key];
  return typeof value === "string" ? value : fallback;
}

function readInt(
  raw: Record<string, unknown>,
  key: string,
  fallback: number,
  min: number,
  max: number,
): number {
  const value = raw[key];
  const parsed =
    typeof value === "number" ? value : typeof value === "string" ? Number(value) : Number.NaN;
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(parsed)));
}

function readFloat(
  raw: Record<string, unknown>,
  key: string,
  fallback: number,
  min: number,
  max: number,
): number {
  const value = raw[key];
  const parsed =
    typeof value === "number" ? value : typeof value === "string" ? Number(value) : Number.NaN;
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

export function parseAdsSearch(raw: Record<string, unknown>): AdsSearch {
  const modeRaw = readString(raw, "mode", DEFAULT_ADS_SEARCH.mode);
  const dirRaw = readString(raw, "dir", DEFAULT_ADS_SEARCH.dir);
  return {
    q: readString(raw, "q", DEFAULT_ADS_SEARCH.q).slice(0, 120),
    status: readString(raw, "status", DEFAULT_ADS_SEARCH.status),
    account: readString(raw, "account", DEFAULT_ADS_SEARCH.account),
    // 语言只接受 ISO-639-1 小写（可带地区子标签）；垃圾值一律回落 "all"（URL 可手写）。
    language: (() => {
      const value = readString(raw, "language", DEFAULT_ADS_SEARCH.language);
      return /^[a-z]{2}(-[a-z]{2})?$/.test(value) ? value : DEFAULT_ADS_SEARCH.language;
    })(),
    minRoas: readFloat(raw, "minRoas", DEFAULT_ADS_SEARCH.minRoas, 0, 1000),
    mode: modeRaw === "paged" ? "paged" : "virtual",
    sort: readString(raw, "sort", DEFAULT_ADS_SEARCH.sort),
    dir: dirRaw === "asc" ? "asc" : "desc",
    page: readInt(raw, "page", DEFAULT_ADS_SEARCH.page, 1, 1_000_000),
    size: readInt(raw, "size", DEFAULT_ADS_SEARCH.size, 1, MAX_SIZE),
  };
}

/** 不可变地局部更新；`patch` 里的值会按同一套规则裁剪。 */
export function patchAdsSearch(prev: AdsSearch, patch: Partial<AdsSearch>): AdsSearch {
  return parseAdsSearch({ ...prev, ...patch, q: patch.q ?? prev.q });
}

/** 序列化为 query 参数；等于默认值的字段一律省略。 */
export function adsSearchToParams(search: AdsSearch): Record<string, string> {
  const params: Record<string, string> = {};
  if (search.q !== DEFAULT_ADS_SEARCH.q) params["q"] = search.q;
  if (search.status !== DEFAULT_ADS_SEARCH.status) params["status"] = search.status;
  if (search.account !== DEFAULT_ADS_SEARCH.account) params["account"] = search.account;
  if (search.language !== DEFAULT_ADS_SEARCH.language) params["language"] = search.language;
  if (search.minRoas !== DEFAULT_ADS_SEARCH.minRoas) params["minRoas"] = String(search.minRoas);
  if (search.mode !== DEFAULT_ADS_SEARCH.mode) params["mode"] = search.mode;
  if (search.sort !== DEFAULT_ADS_SEARCH.sort) params["sort"] = search.sort;
  if (search.dir !== DEFAULT_ADS_SEARCH.dir) params["dir"] = search.dir;
  if (search.page !== DEFAULT_ADS_SEARCH.page) params["page"] = String(search.page);
  if (search.size !== DEFAULT_ADS_SEARCH.size) params["size"] = String(search.size);
  return params;
}

/** 供 TanStack Router 的 stringifySearch 使用：输出 `?a=1&b=2`，默认值不出现。
 *  参数放宽到 `object`：路由回调传进来的是 `Record<string, unknown>`，而业务侧通常直接传
 *  `AdsSearch`（接口没有索引签名，无法赋给 Record<string, unknown>）。 */
export function adsSearchToQueryString(search: object): string {
  const params = adsSearchToParams(parseAdsSearch(search as Record<string, unknown>));
  const usp = new URLSearchParams(params);
  const qs = usp.toString();
  return qs.length > 0 ? `?${qs}` : "";
}

/** 当前状态与默认值的差异摘要，用于「已筛选」提示条。 */
export function describeActiveFilters(search: AdsSearch): readonly string[] {
  const parts: string[] = [];
  if (search.q.trim().length > 0) parts.push(`关键词「${search.q.trim()}」`);
  if (search.status !== "all") parts.push(`状态 = ${search.status}`);
  if (search.account !== "all") parts.push(`账户 = ${search.account}`);
  if (search.language !== DEFAULT_ADS_SEARCH.language) parts.push(`语言 = ${search.language}`);
  if (search.minRoas > 0) parts.push(`Shopify ROAS ≥ ${search.minRoas}`);
  return parts;
}
